'use client';

import { c as _c } from "react/compiler-runtime";
import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import { useClientABSmartlyExperiments } from '@repo/platform-absmartly/hooks/useClientABSmartlyExperiments';
import { ABTestVariant } from '@repo/platform-absmartly/utils/variants';
import { usePageAction } from '@repo/platform-analytics/hooks/usePageAction';
import { useRouter } from 'next/navigation';
import { useSessionSync } from '@/entities/session/model/sessionSyncContext';
import { CARDFINDER_THINKING_INTERSTITIAL_ID } from '@/shared/config/experiments';
import type { BranchId } from '@/shared/config/funnel/constants';
import { FUNNEL_ENTRY_PARAM, FUNNEL_ENTRY_VALUE, RECOMMENDATIONS_PATH } from '@/shared/config/funnel/flowConstants';
import { withCurrentSearchParams } from '@/shared/lib/searchParams';
import { useFunnelConfigVersion, useFunnelFlattenedConfig } from './Context/hooks';

/**
 * The end of a funnel run: commit it, then show the results.
 *
 * This is the funnel's completion signal — reached only from `resumeAfterStep`
 * when the current step ends the branch.
 *
 * `submittedAnswers` must be the answer set *including* the final step's answer.
 * The caller has it; the caller's store snapshot predates the final
 * `SUBMIT_ANSWER` dispatch, so it can't be read back from there.
 */
export function useFinishFunnel() {
  const $ = _c(9);
  const router = useRouter();
  const funnelFlattenedConfig = useFunnelFlattenedConfig();
  const funnelConfigVersion = useFunnelConfigVersion();
  const {
    completeRun
  } = useSessionSync();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      actionName: "cc_recs_submit",
      actionSection: "cardfinder_flow",
      actionType: "submit"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const firePageAction = usePageAction(t0);
  const {
    results,
    isReady
  } = useClientABSmartlyExperiments(CARDFINDER_THINKING_INTERSTITIAL_ID);
  const variant = results[0];
  let t1;
  if ($[1] !== completeRun || $[2] !== firePageAction || $[3] !== funnelConfigVersion || $[4] !== funnelFlattenedConfig || $[5] !== isReady || $[6] !== router || $[7] !== variant) {
    t1 = async (branchId, submittedAnswers) => {
      firePageAction({
        additional_attributes: {
          branch_id: branchId
        }
      });
      const result = await completeRun(funnelFlattenedConfig, funnelConfigVersion, submittedAnswers);
      if (result === "version-mismatch") {
        return;
      }
      if (isReady && variant === ABTestVariant.Treatment) {
        router.push(withCurrentSearchParams(`${RECOMMENDATIONS_PATH}?${FUNNEL_ENTRY_PARAM}=${FUNNEL_ENTRY_VALUE}`));
      } else {
        router.push(withCurrentSearchParams(`${RECOMMENDATIONS_PATH}`));
      }
    };
    $[1] = completeRun;
    $[2] = firePageAction;
    $[3] = funnelConfigVersion;
    $[4] = funnelFlattenedConfig;
    $[5] = isReady;
    $[6] = router;
    $[7] = variant;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}